/**
 * cashBookDataSource.ts — WatermelonDB → CashBookInput bridge.
 *
 * All three sources are on the **cash basis** — when money actually moved:
 *  - customer receipts by `payments.received_at` (resolved to the parent
 *    invoice's type so refunds are recognised as outflows, and to the store so
 *    another store's payments don't leak in — `payments` carries no store_id);
 *  - supplier payments by `supplier_payments.payment_date`;
 *  - expenses by `expenses.expense_date`.
 *
 * This mirrors the correction Sales/Day Close received: money is dated when it
 * arrives or leaves, not when the underlying document was raised.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, Payment, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { SupplierPayment } from '@/db/models/Inventory';
import { Expense } from '@/db/models/System';
import type { CashBookInput } from './cashBook';

/** Load a store's money movements for the half-open range `[from, to)`. */
export async function loadCashBook(
  storeId: string,
  from: Date,
  to: Date,
): Promise<CashBookInput> {
  const fromMs = from.getTime();
  const toMs = to.getTime();

  // ── Customer receipts: payments by received_at, scoped via parent invoice ──
  const paymentRows = await database.get<Payment>('payments').query(
    Q.where('is_deleted', false),
    Q.where('received_at', Q.gte(fromMs)),
    Q.where('received_at', Q.lt(toMs)),
  ).fetch();

  const parentIds = [...new Set(paymentRows.map((p) => p.invoiceId))];
  const parents = await fetchInChunks(parentIds, (batch) =>
    database.get<Invoice>('invoices').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('store_id', storeId),
      Q.where('is_deleted', false),
      Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    ).fetch(),
  );
  const parentType = new Map(parents.map((i) => [i.id, i.type]));

  // ── Supplier payments (money out) ──
  const supplierPaymentRows = await database.get<SupplierPayment>('supplier_payments').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('payment_date', Q.gte(fromMs)),
    Q.where('payment_date', Q.lt(toMs)),
  ).fetch();

  // ── Expenses (money out), by business date ──
  const expenseRows = await database.get<Expense>('expenses').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('expense_date', Q.gte(fromMs)),
    Q.where('expense_date', Q.lt(toMs)),
  ).fetch();

  return {
    // Drop payments whose invoice belongs to another store, was voided, or has
    // vanished — parentType only holds this store's live invoices.
    receipts: paymentRows.flatMap((p) => {
      const invoiceType = parentType.get(p.invoiceId);
      return invoiceType
        ? [{ date: p.receivedAt, amount: p.amount, method: p.method, invoiceType }]
        : [];
    }),
    supplierPayments: supplierPaymentRows.map((p) => ({
      date: p.paymentDate,
      amount: p.amount,
      method: p.method,
    })),
    expenses: expenseRows.map((e) => ({
      date: e.expenseDate,
      amount: e.amount,
      method: e.paymentMethod ?? 'UNSPECIFIED',
    })),
  };
}
